/**
 * IssueFieldSet — 이슈 게시판 전용 옵션 (상태/우선순위/마감일/담당자)
 *
 * PostCreateModal에서 사용하는 서브폼 컴포넌트.
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import {
  IssueStatus, IssuePriority,
  ISSUE_STATUS_CONFIG, ISSUE_PRIORITY_CONFIG,
  ISSUE_STATUS_LIST, ISSUE_PRIORITY_LIST,
  getIssueStatusLabel, getIssuePriorityLabel,
} from '../../../models/Board';
import { cn } from '../../../lib/utils';
import { Calendar, User } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

export interface ChannelMember {
  id: string;
  name: string;
  photoUrl?: string | null;
}

interface IssueFieldSetProps {
  issueStatus: IssueStatus;
  onIssueStatusChange: (value: IssueStatus) => void;
  issuePriority: IssuePriority;
  onIssuePriorityChange: (value: IssuePriority) => void;
  issueDueDate: string;
  onIssueDueDateChange: (value: string) => void;
  assigneeId: string;
  onAssigneeIdChange: (value: string) => void;
  channelMembers: ChannelMember[];
}

const IssueFieldSet: React.FC<IssueFieldSetProps> = ({
  issueStatus, onIssueStatusChange,
  issuePriority, onIssuePriorityChange,
  issueDueDate, onIssueDueDateChange,
  assigneeId, onAssigneeIdChange,
  channelMembers,
}) => (
  <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
    <span className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
      <Trans>Issue settings</Trans>
    </span>

    {/* 상태 + 우선순위 */}
    <div className="grid grid-cols-2 gap-3">
      {/* 상태 */}
      <div className="space-y-1.5">
        <span className="text-meta-sm text-muted-foreground"><Trans>Status</Trans></span>
        <div className="flex flex-wrap gap-1">
          {ISSUE_STATUS_LIST.filter(s => s !== 'cancelled').map(s => {
            const cfg = ISSUE_STATUS_CONFIG[s];
            const isActive = issueStatus === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => onIssueStatusChange(s)}
                className={cn(
                  'px-2 py-1 rounded-md text-meta-sm font-medium transition-all cursor-pointer border',
                  isActive
                    ? `border-current ${cfg.bgColor} ${cfg.color}`
                    : 'border-transparent hover:bg-muted text-muted-foreground',
                )}
              >
                {getIssueStatusLabel(s)}
              </button>
            );
          })}
        </div>
      </div>

      {/* 우선순위 */}
      <div className="space-y-1.5">
        <span className="text-meta-sm text-muted-foreground"><Trans>Priority</Trans></span>
        <div className="flex flex-wrap gap-1">
          {ISSUE_PRIORITY_LIST.map(p => {
            const cfg = ISSUE_PRIORITY_CONFIG[p];
            const isActive = issuePriority === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => onIssuePriorityChange(p)}
                className={cn(
                  'px-2 py-1 rounded-md text-meta-sm font-medium transition-all cursor-pointer border',
                  isActive
                    ? `border-current ${cfg.color}`
                    : 'border-transparent hover:bg-muted text-muted-foreground',
                )}
              >
                <span className="mr-0.5">{cfg.icon}</span> {getIssuePriorityLabel(p)}
              </button>
            );
          })}
        </div>
      </div>
    </div>

    {/* 마감일 + 담당자 */}
    <div className="grid grid-cols-2 gap-3">
      {/* 마감일 */}
      <div className="space-y-1.5">
        <span className="text-meta-sm text-muted-foreground flex items-center gap-1">
          <Calendar className="size-3" /> <Trans>Due Date</Trans>
        </span>
        <input
          type="date"
          value={issueDueDate}
          onChange={e => onIssueDueDateChange(e.target.value)}
          className="w-full h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* 담당자 (채널 멤버 피커) */}
      <div className="space-y-1.5">
        <span className="text-meta-sm text-muted-foreground flex items-center gap-1">
          <User className="size-3" /> <Trans>Assignee</Trans>
        </span>
        <Select
          value={assigneeId || '__unassigned__'}
          onValueChange={v => onAssigneeIdChange(v === '__unassigned__' ? '' : v)}
        >
          <SelectTrigger className="w-full h-8 text-xs shadow-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__unassigned__"><Trans>Unassigned</Trans></SelectItem>
            {channelMembers.map(m => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

export default IssueFieldSet;
